import moment from 'moment';
import { propertyTypeParams } from 'helpers/research/filter_helpers';
import { leaseRateMappings } from 'helpers/comps/global_table_constants';

export const keys = {
  apnNumber: 'q[research_property_apn_cont]',
  assetClasses: 'q[property_type_in]',
  availabilities: 'q[research_property_deal_status_id_in]',
  buildingClasses: 'q[research_property_building_class_in]',
  buildingNumber: 'q[research_property_address_start]',
  cities: 'q[research_property_city_in]',
  investmentTypes: 'q[research_property_investment_type_in]',
  leasePage: 'lease_page',
  maxAvailableSF: 'q[research_property_available_sf_lteq]',
  market: 'q[research_property_market_cont]',
  microMarket: 'q[research_property_micro_market_cont]',
  maxBuildingSize: 'q[research_property_building_size_lteq]',
  maxCeilingHeight: 'q[research_property_ceiling_height_lteq]',
  maxNumUnits: 'q[research_property_number_of_units_lteq]',
  maxNumKeys: 'q[research_property_number_of_units_lteq]',
  maxYearBuilt: 'q[research_property_year_built_lteq]',
  minAvailableSF: 'q[research_property_available_sf_gteq]',
  minBuildingSize: 'q[research_property_building_size_gteq]',
  minNumUnits: 'q[research_property_number_of_units_gteq]',
  minNumKeys: 'q[research_property_number_of_units_gteq]',
  minYearBuilt: 'q[research_property_year_built_gteq]',
  mixCeilingHeight: 'q[research_property_ceiling_height_gteq]',
  salePage: 'sale_page',
  searchText: 'q[search_text_cont]',
  state: 'q[research_property_state_cont]',
  street: 'q[research_property_address_cont]',
  subMarket: 'q[research_property_submarket_cont]',
  databaseId: 'q[database_id_eq]',
  zip: 'q[research_property_zip_cont]',
  county: 'q[research_property_county_cont]'
};

export const industrialKeys = {
  availableRailProvider: 'q[research_property_available_rail_provider_eq]',
  driveThroughAccess: 'q[research_property_drive_through_access_eq]',
  lotBorderType: 'q[research_property_lot_border_type_eq]',
  lotStabilization: 'q[research_property_lot_stabilization_eq]',
  maxAcresStabilized: 'q[research_property_min_yard_lteq]',
  maxBridgeCraneSpan: 'q[research_property_max_bridge_crane_span_lteq]',
  maxJibCraneSpan: 'q[research_property_jib_crane_span_lteq]',
  maxNumBridgeCranes: 'q[research_property_number_of_bridge_cranes_lteq]',
  maxNumJibCranes: 'q[research_property_number_of_jib_cranes_lteq]',
  maxReadyCapacity: 'q[research_property_max_ready_crane_capacity_gteq]',
  maxReadySpan: 'q[research_property_max_ready_crane_span_lteq]',
  minAcresStabilized: 'q[research_property_min_yard_gteq]',
  minBridgeCraneSpan: 'q[research_property_max_bridge_crane_span_gteq]',
  minJibCraneSpan: 'q[research_property_jib_crane_span_gteq]',
  minNumBridgeCranes: 'q[research_property_number_of_bridge_cranes_gteq]',
  minNumJibCranes: 'q[research_property_number_of_jib_cranes_gteq]',
  minReadyCapacity: 'q[research_property_min_ready_crane_capacity_lteq]',
  minReadySpan: 'q[research_property_max_ready_crane_span_gteq]',
  ready: 'q[research_property_ready_eq]',
  served: 'q[research_property_served_eq]',
  servedRailProvider: 'q[research_property_served_rail_provider_eq]'
};

export const saleKeys = {
  maxSalesPrice: 'q[price_lteq]',
  minSalesPrice: 'q[price_gteq]',
  maxSalesDate: 'q[sale_date_lteq]',
  minSalesDate: 'q[sale_date_gteq]',
  maxSfPrice: 'q[price_per_sf_lteq]',
  minSfPrice: 'q[price_per_sf_gteq]',
  maxUnitPrice: 'q[price_per_unit_lteq]',
  minUnitPrice: 'q[price_per_unit_gteq]',
  maxCapRate: 'q[cap_rate_pct_lteq]',
  minCapRate: 'q[cap_rate_pct_gteq]',
  saleSort: 'sort[sale]'
};

export const leaseKeys = {
  leaseSort: 'sort[lease]',
  leaseSpaceType: 'q[space_type_id_eq]',
  leaseType: 'q[lease_type_id_eq]',
  maxLeaseDate: 'q[lease_date_lteq]',
  minLeaseDate: 'q[lease_date_gteq]',
  maxLeaseExecutionDate: 'q[lease_execution_date_lteq]',
  minLeaseExecutionDate: 'q[lease_execution_date_gteq]',
  maxLeaseExpirationDate: 'q[lease_expiration_date_lteq]',
  minLeaseExpirationDate: 'q[lease_expiration_date_gteq]',
  maxLeaseSize: 'q[size_lteq]',
  minLeaseSize: 'q[size_gteq]'
};

/*
 * Retrieves the options needed to build a Ransack query for comps.
 *
 * @param {Object} query - State of the user query from the store
 * @param {Object} scope - Additional scoping filters.  Currently supports {number} databaseId.
 * @param {string} saleOrLease - Either 'sale' or 'lease'
 */
export function retrieveFilterOptions(query, scope, saleOrLease) {
  keys.maxLotSize = (query.propertyFilter.lotUnits === 'sf') ?
    'q[research_property_lot_size_sf_lteq]' : 'q[research_property_lot_size_lteq]';
  keys.minLotSize = (query.propertyFilter.lotUnits === 'sf') ?
    'q[research_property_lot_size_sf_gteq]' : 'q[research_property_lot_size_gteq]';

  const { driveThroughAccess, served, ready, ...industrialFilters } = query.industrialFilter;
  const { cities, currentCity, ...addressFilter } = query.addressFilter;
  const { minNumKeys, minNumUnits, maxNumKeys, maxNumUnits, ...propertyFilter } = query.propertyFilter;

  let filterOptions = { ...addressFilter, ...propertyFilter, ...industrialFilters };

  filterOptions.minNumUnits = minNumKeys || minNumUnits;
  filterOptions.maxNumUnits = maxNumKeys || maxNumUnits;

  if (driveThroughAccess) {
    filterOptions.driveThroughAccess = driveThroughAccess;
  }

  if (served) {
    filterOptions.served = served;
  }

  if (ready) {
    filterOptions.ready = ready;
  }

  if (saleOrLease === 'lease') {
    const leaseRate = leaseRateMappings[query.leaseFilter.leaseRateUnits];
    keys.maxLeasePrice = leaseRate.max;
    keys.minLeasePrice = leaseRate.min;

    const { maxLeasePrice, minLeasePrice, minLeaseDate, maxLeaseDate, ...leaseFilter } = query.leaseFilter;

    if (maxLeasePrice > 0) {
      filterOptions.maxLeasePrice = maxLeasePrice * leaseRate.conversion;
    }

    if (minLeasePrice > 0) {
      filterOptions.minLeasePrice = minLeasePrice * leaseRate.conversion;
    }

    if (minLeaseDate) {
      filterOptions.minLeaseDate = moment(minLeaseDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    }

    if (maxLeaseDate) {
      filterOptions.maxLeaseDate = moment(maxLeaseDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    }

    filterOptions = { ...filterOptions, ...leaseFilter };
    filterOptions.leasePage = query.leasePage;
    filterOptions.leaseSort = query.leaseSort || query.sort;
  } else {
    const { minSalesDate, maxSalesDate, ...saleFilter } = query.saleFilter;

    if (minSalesDate) {
      filterOptions.minSalesDate = moment(minSalesDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    }

    if (maxSalesDate) {
      filterOptions.maxSalesDate = moment(maxSalesDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    }

    filterOptions = { ...filterOptions, ...saleFilter };
    filterOptions.salePage = query.salePage;
    filterOptions.saleSort = query.saleSort || query.sort;
  }

  if (currentCity) {
    filterOptions.cities = [...cities, currentCity];
  } else {
    filterOptions.cities = cities;
  }

  filterOptions.availabilities = query.availabilities;
  filterOptions.buildingClasses = query.buildingClasses;
  filterOptions.investmentTypes = query.investmentTypes;
  filterOptions.searchText = query.searchText;

  filterOptions.assetClasses = propertyTypeParams(query.assetClasses);

  if (scope) {
    filterOptions.databaseId = scope.databaseId;
  }

  return filterOptions;
}
